import { useMemo, useState, useRef, useEffect } from 'react';
import type { ReactNode } from 'react';
import { PlayerAvatar } from './PlayerAvatar';
import { fonts, glassBorder, glassText, brandGradient } from '../styles/overlayTokens';
import type { HealthConfig } from '../types/widgets';
import { useFitToWidth } from '../hooks/useFitToWidth';

// ─── HealthPips ──────────────────────────────────────────────────────────────

interface HealthPipsProps {
  current: number;
  max: number;
  label: string;
}

export function HealthPips({ current, max, label }: HealthPipsProps) {
  const pips = useMemo(() => {
    if (max > 10) return null;
    return Array.from({ length: max }, (_, i) => (
      <span
        key={i}
        style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: i < current ? glassText.primary : 'rgba(255,255,255,0.15)',
        }}
      />
    ));
  }, [max, current]);

  if (pips === null) {
    return (
      <span style={{ fontSize: 14, fontWeight: 500, color: glassText.primary, fontVariantNumeric: 'tabular-nums' }}>
        {label} {current}/{max}
      </span>
    );
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ fontSize: 14, textTransform: 'uppercase', color: glassText.muted, marginRight: 4 }}>{label}</span>
      {pips}
    </span>
  );
}

// ─── HealthBar ───────────────────────────────────────────────────────────────

interface HealthBarProps {
  current: number;
  max: number;
  stage: 1 | 2;
  config: HealthConfig;
  /** Width of the bar track. Defaults to 120. Pass `'100%'` for full-width vertical contexts. */
  width?: number | string;
  /**
   * When 'right', mirrors the bar layout for P2: order becomes [number][bar track][LABEL],
   * fill grows from the right, and the dual-stage separator moves to the left edge.
   */
  align?: 'left' | 'right';
}

export function HealthBar({ current, max, stage, config, width = 120, align }: HealthBarProps) {
  const ratio = Math.max(0, Math.min(1, current / max));
  const barColor = stage === 2 ? (config.secondStageColor ?? '#FF3FAF') : (config.color ?? '#3b82f6');

  const isFluid = width === '100%';
  const isMirrored = align === 'right';

  return (
    <span style={{
      display: isFluid ? 'flex' : 'inline-flex',
      alignItems: 'center',
      gap: 6,
      // Reverse child order ([number][bar][LABEL]) when mirrored for P2
      flexDirection: isMirrored ? 'row-reverse' : 'row',
      ...(isFluid ? { width: '100%' } : {}),
    }}>
      <span style={{ fontSize: 14, textTransform: 'uppercase', color: glassText.muted, flexShrink: 0, ...(isFluid ? { minWidth: 56 } : {}) }}>{config.label}</span>
      <span style={{
        position: 'relative',
        ...(isFluid ? { flex: 1, minWidth: 0, display: 'block' } : { width }),
        height: 8,
        borderRadius: 4,
        background: 'rgba(255,255,255,0.1)',
        overflow: 'hidden',
      }}>
        <span style={{
          position: 'absolute',
          left: 0,
          top: 0,
          height: '100%',
          borderRadius: 4,
          background: barColor,
          transform: `scaleX(${ratio})`,
          // Mirrored: fill grows from the right so it drains left-ward as health falls
          transformOrigin: isMirrored ? 'right' : 'left',
          transition: 'transform 0.4s ease-out, background 0.3s',
          willChange: 'transform',
          width: '100%',
        }} />
        {/* Dual-stage separator: right edge normally, left edge when mirrored */}
        {config.secondStageMax && stage === 1 && (
          <span style={{
            position: 'absolute',
            [isMirrored ? 'left' : 'right']: 0,
            top: 0,
            width: 1,
            height: '100%',
            background: 'rgba(255,255,255,0.3)',
          }} />
        )}
      </span>
      {/* Number: right-aligned normally, left-aligned when mirrored (it sits on the left) */}
      <span style={{ fontSize: 14, color: glassText.secondary, fontVariantNumeric: 'tabular-nums', minWidth: 28, textAlign: isMirrored ? 'left' : 'right' }}>
        {current}
      </span>
    </span>
  );
}

// ─── SecurityBadge ────────────────────────────────────────────────────────────

/** Named color constants for the three security states — shared vocabulary with the web caster console. */
const SECURITY_COLORS = {
  healthy: { main: '#22D3EE', glow: 'rgba(34,211,238,0.35)', tintFilter: 'brightness(0.8) sepia(1) saturate(2) hue-rotate(155deg)', pulseClass: 'anim-security-pulse-healthy' },
  warning: { main: '#FBBF24', glow: 'rgba(251,191,36,0.35)', tintFilter: 'brightness(0.85) sepia(1) saturate(2.5) hue-rotate(-5deg)', pulseClass: 'anim-security-pulse-warning' },
  // brightness(0.4) is intentionally low — produces a dark, ominous red tint for the "breached" state where security is at 0.
  breached: { main: '#EF4444', glow: 'rgba(239,68,68,0.4)', tintFilter: 'brightness(0.4) sepia(1) saturate(5) hue-rotate(345deg)', pulseClass: 'anim-security-pulse-breached' },
} as const;

function getSecurityState(current: number): 'healthy' | 'warning' | 'breached' {
  if (current <= 0) return 'breached';
  if (current === 1) return 'warning';
  return 'healthy';
}

interface SecurityBadgeProps {
  current: number;
  config: HealthConfig;
  align?: 'left' | 'right';
  /** Badge octagon size in px. Defaults to 128. */
  size?: number;
}

export function SecurityBadge({ current, config, align: _align = 'left', size = 128 }: SecurityBadgeProps) {
  const state = getSecurityState(current);
  const colors = SECURITY_COLORS[state];

  /** Track previous current to detect the breach transition (drop to 0). */
  const prevCurrentRef = useRef(current);
  const [breachOneShot, setBreachOneShot] = useState(false);

  useEffect(() => {
    if (current <= 0 && prevCurrentRef.current > 0) {
      setBreachOneShot(true);
      const t = setTimeout(() => setBreachOneShot(false), 500);
      prevCurrentRef.current = current;
      return () => clearTimeout(t);
    }
    prevCurrentRef.current = current;
  }, [current]);

  // Breach: one-shot shake only — continuous glow is handled by the image's drop-shadow filter
  const outerAnimClass = state === 'breached' && breachOneShot ? 'anim-shake' : '';

  // Scale all inner dimensions proportionally to the badge size
  const s = size / 128;

  return (
    <div
      className={outerAnimClass}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      {/* Security icon */}
      <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
        {/* Security logo — tinted to state color with shape-conforming glow */}
        <img
          className={colors.pulseClass}
          src={config.iconPath ?? '/digimon-security-icon.png'}
          alt="Security"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            filter: `${colors.tintFilter} drop-shadow(0 0 ${Math.round(10 * s)}px ${colors.glow}) drop-shadow(0 0 ${Math.round(20 * s)}px ${colors.glow})`,
            transition: 'filter 0.4s ease-out',
          }}
        />
        {/* Numeric counter — backed pill for guaranteed broadcast legibility */}
        <span
          style={{
            position: 'absolute',
            bottom: Math.round(6 * s),
            right: Math.round(6 * s),
            minWidth: Math.round(36 * s),
            height: Math.round(36 * s),
            borderRadius: Math.round(18 * s),
            background: 'rgba(10, 10, 14, 0.92)',
            border: `${Math.round(2 * s)}px solid ${colors.main}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: Math.round(22 * s),
            fontWeight: 800,
            color: colors.main,
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1,
            padding: `0 ${Math.round(6 * s)}px`,
            boxSizing: 'border-box',
          }}
        >
          {current}
        </span>
      </div>
    </div>
  );
}

// ─── Pill ─────────────────────────────────────────────────────────────────────

interface PillProps {
  children: ReactNode;
}

export function Pill({ children }: PillProps) {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      padding: '4px 12px',
      borderRadius: 6,
      background: 'rgba(255,255,255,0.06)',
      border: '1px solid rgba(255,255,255,0.10)',
      fontSize: 14,
      fontWeight: 500,
      color: glassText.primary,
      fontVariantNumeric: 'tabular-nums',
      whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  );
}

// ─── HealthDisplay ────────────────────────────────────────────────────────────

interface HealthDisplayProps {
  current: number;
  max: number;
  stage: 1 | 2;
  config: HealthConfig;
  /** Forwarded to HealthBar when displayMode === 'bar'. Defaults to 120. */
  barWidth?: number | string;
  /** Forwarded to SecurityBadge to mirror layout for P2. */
  align?: 'left' | 'right';
  /** Forwarded to SecurityBadge octagon size in px. Defaults to 128. */
  badgeSize?: number;
}

export function HealthDisplay({ current, max, stage, config, barWidth, align, badgeSize }: HealthDisplayProps) {
  if (config.displayMode === 'bar') {
    return <Pill><HealthBar current={current} max={max} stage={stage} config={config} width={barWidth} align={align} /></Pill>;
  }
  // Security badge has its own shape — returned directly without Pill wrapper.
  // This check must come before the implicit pips fallback.
  if (config.displayMode === 'security') {
    return <SecurityBadge current={current} config={config} align={align} size={badgeSize} />;
  }
  return <Pill><HealthPips current={current} max={max} label={config.label} /></Pill>;
}

// ─── useScorePulse ────────────────────────────────────────────────────────────

/**
 * Returns a CSS animation class name that pulses when `score` changes.
 * The class is set to `'anim-pulse'` for 500ms then cleared.
 * Handles cleanup internally so callers need no teardown logic.
 */
export function useScorePulse(score: number): string {
  const [animClass, setAnimClass] = useState('');
  const prevScore = useRef(score);

  useEffect(() => {
    if (score !== prevScore.current) {
      setAnimClass('anim-pulse');
      const t = setTimeout(() => setAnimClass(''), 500);
      prevScore.current = score;
      return () => clearTimeout(t);
    }
  }, [score]);

  return animClass;
}

// ─── ScoreBadge ───────────────────────────────────────────────────────────────

interface ScoreBadgeProps {
  score: number;
  isLeading: boolean;
}

export function ScoreBadge({ score, isLeading }: ScoreBadgeProps) {
  return (
    <div style={{
      width: 48,
      height: 48,
      borderRadius: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 28,
      fontWeight: 800,
      color: glassText.primary,
      background: 'rgba(255,255,255,0.06)',
      border: isLeading ? '2px solid transparent' : `1px solid ${glassBorder.light}`,
      backgroundImage: isLeading ? `linear-gradient(rgba(255,255,255,0.06), rgba(255,255,255,0.06)), ${brandGradient.horizontal}` : undefined,
      backgroundOrigin: isLeading ? 'border-box' : undefined,
      backgroundClip: isLeading ? 'padding-box, border-box' : undefined,
    }}>
      {score}
    </div>
  );
}

// ─── StackedCardAvatars ───────────────────────────────────────────────────────

/**
 * ChronoCore pilot/core avatar treatment: two equal-size circles, pilot at top-left (back),
 * core at bottom-right overlapping the pilot by ~58%. Core gets a thin border ring so the
 * overlap reads cleanly on the dark glass background.
 *
 * Each circle clips its img via overflow:hidden + border-radius on the wrapper, then applies
 * scale(1.5) anchored at center so the character portrait fills the circle with balanced
 * vertical framing. objectPosition:'center' centers the objectFit:cover crop vertically.
 *
 * When only one URL is supplied the single circle renders without offset.
 *
 * When `mirror` is true the stack is horizontally flipped: pilot sits top-right, core
 * bottom-left. Use for P2 so both stacks "face inward" toward the center score badge.
 *
 * Size / footprint reference (slot → circle diameter → combined container, 1.42× footprint at 58% overlap):
 *   standard (PlayerWidget):              112px circles → 159px container (+ 8px margin = 175px in 180px bubble → ~10px gap each side)
 *   large compact (SidePlayersWidget):    200px circles → 284px container (compact panel 330px − 40px h-padding = 290px usable;  6px margin)
 *   large medium  (SidePlayersWidget):    216px circles → 307px container (medium panel 360px − 40px h-padding = 320px usable; 13px margin)
 *   large full    (SidePlayersWidget):    248px circles → 352px container (full panel 400px − 40px h-padding = 360px usable;  8px margin)
 */
export function StackedCardAvatars({
  pilotCardUrl,
  coreCardUrl,
  circleSize,
  mirror = false,
  avatarShape,
}: {
  pilotCardUrl?: string | null;
  coreCardUrl?: string | null;
  circleSize: number;
  /** When true, mirrors the stack horizontally: pilot top-right, core bottom-left. Use for P2 panels. */
  mirror?: boolean;
  /** Controls wrapper corner radius. 'square' uses the same 15% ratio as PlayerAvatar; 'circle' (default) stays fully round. */
  avatarShape?: 'circle' | 'square';
}) {
  const hasPilot = pilotCardUrl != null && pilotCardUrl !== '';
  const hasCore = coreCardUrl != null && coreCardUrl !== '';
  const hasBoth = hasPilot && hasCore;

  // Corner radius: matches PlayerAvatar's square formula (size * 0.15) so shapes look consistent
  // when the caster switches between circle/square. Falls back to fully round for 'circle' (default).
  const wrapperRadius = avatarShape === 'square' ? Math.round(circleSize * 0.15) : '50%';

  // ~58% overlap: tighter overlap allows larger circle diameters within each slot's max footprint.
  // standard 112px → overlap=65 → container=159px; large 204px → overlap=118 → container=290px.
  const overlap = hasBoth ? Math.round(circleSize * 0.58) : 0;
  const offset = hasBoth ? circleSize - overlap : 0;
  const containerSize = hasBoth ? offset + circleSize : circleSize;
  // Scale border thickness with circle size (2px at 56px, 3px at 110px)
  const borderWidth = circleSize >= 100 ? 3 : 2;

  return (
    <div style={{ position: 'relative', width: containerSize, height: containerSize, flexShrink: 0 }}>
      {/* Pilot — back circle.
          Default (P1): top-left. Mirrored (P2): top-right — pilot shifts to left:offset so its
          right edge aligns with the container's right edge.
          Wrapper clips the scaled img to the circle; img uses scale(1.5) centered so the
          character portrait fills the circle with balanced vertical framing. */}
      {hasPilot && (
        <div
          style={{
            position: 'absolute',
            left: mirror && hasBoth ? offset : 0,
            top: 0,
            width: circleSize,
            height: circleSize,
            borderRadius: wrapperRadius,
            overflow: 'hidden',
          }}
        >
          <img
            src={pilotCardUrl as string}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center 15%',
              display: 'block',
              transform: 'scale(1.5)',
              transformOrigin: 'center 15%',
            }}
          />
        </div>
      )}
      {/* Core — front circle, overlapping pilot.
          Default (P1): bottom-right (left:offset, top:offset).
          Mirrored (P2): bottom-left (left:0, top:offset) so the stack reads right-to-left,
          with core closest to the center VS badge.
          Border ring is on the clipping wrapper (border-box) so it frames the circle
          crisply and does not interfere with objectFit or the scale transform. */}
      {hasCore && (
        <div
          style={{
            position: 'absolute',
            left: hasBoth && !mirror ? offset : 0,
            top: hasBoth ? offset : 0,
            width: circleSize,
            height: circleSize,
            borderRadius: wrapperRadius,
            overflow: 'hidden',
            border: `${borderWidth}px solid rgba(255,255,255,0.30)`,
            boxSizing: 'border-box',
            zIndex: 1,
          }}
        >
          <img
            src={coreCardUrl as string}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center 15%',
              display: 'block',
              transform: 'scale(1.5)',
              transformOrigin: 'center 15%',
            }}
          />
        </div>
      )}
    </div>
  );
}

// ─── AutoShrinkText ───────────────────────────────────────────────────────────

interface AutoShrinkTextProps {
  text: string;
  maxFontSize: number;
  minFontSize: number;
  fontWeight?: number;
  lineHeight?: number;
  color?: string;
  textAlign?: 'left' | 'right' | 'center';
  /** Override font family. Defaults to inheriting the parent's font (fonts.body via root container).
   *  Pass fonts.display for player names to use Fjalla One — the condensed measurement must match
   *  the rendered font, so the probe span also receives this value for accurate width sampling. */
  fontFamily?: string;
}

/**
 * Renders text at the largest font size that fits the container width on a single line.
 * Algorithm:
 *   1. Try each font size from maxFontSize down to minFontSize (1px steps).
 *   2. First size whose single-line scrollWidth ≤ container clientWidth is used → nowrap.
 *   3. If still overflowing at minFontSize → wrap at minFontSize with break-word.
 *   Never truncates with ellipsis.
 *
 * Measurement uses a hidden absolutely-positioned probe inside the container so the loop
 * runs entirely in useLayoutEffect (before paint — no first-render flicker). A ResizeObserver
 * re-fits whenever the container width changes (e.g. side panel compact↔full toggle).
 * Fit machinery (container/probe wiring, ResizeObserver lifecycle, descending-candidate loop)
 * is shared with GaHighlightsRow (DeckOverlayPanels.tsx) via `useFitToWidth`.
 */
export function AutoShrinkText({
  text,
  maxFontSize,
  minFontSize,
  fontWeight,
  lineHeight,
  color,
  textAlign,
  fontFamily,
}: AutoShrinkTextProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const probeRef = useRef<HTMLSpanElement>(null);

  const { size: fontSize, fits } = useFitToWidth(containerRef, probeRef, {
    min: minFontSize,
    max: maxFontSize,
    step: 1,
    applyToProbe: size => {
      // Setting fontSize + immediately reading scrollWidth (in measureProbe) forces a
      // synchronous layout for this element, giving an accurate single-line width per candidate.
      if (probeRef.current) probeRef.current.style.fontSize = `${size}px`;
    },
    measureProbe: () => probeRef.current?.scrollWidth ?? 0,
    deps: [text, maxFontSize, minFontSize, fontWeight, lineHeight, fontFamily],
  });
  const wrap = !fits;

  return (
    <div ref={containerRef} style={{ position: 'relative', minWidth: 0, width: '100%' }}>
      {/* Hidden single-line probe: out of layout flow, used only for width measurement.
          Shares fontWeight/lineHeight with the visible text for accurate metrics. */}
      <span
        ref={probeRef}
        aria-hidden
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          visibility: 'hidden',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          fontWeight,
          lineHeight,
          fontFamily,
        }}
      >
        {text}
      </span>
      {/* Visible text at the fitted size — wraps at minFontSize if it still overflows */}
      <div
        style={{
          fontSize,
          fontWeight,
          lineHeight,
          color,
          textAlign,
          fontFamily,
          ...(wrap
            ? {
                whiteSpace: 'normal',
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
              }
            : { whiteSpace: 'nowrap' }),
        }}
      >
        {text}
      </div>
    </div>
  );
}

// ─── PlayerInfoBlock ──────────────────────────────────────────────────────────

interface PlayerInfoBlockProps {
  name: string;
  archetype?: string;
  record?: string;
  photoUrl?: string | null;
  avatarSize?: number;
  showPhoto?: boolean;
  glowActive?: boolean;
  health?: number;
  healthStage?: 1 | 2;
  healthConfig?: HealthConfig | null;
  showHealth?: boolean;
  /** Text alignment of the info column. Use 'right' for mirrored P2, 'center' for side panels. Defaults to 'left'. */
  align?: 'left' | 'right' | 'center';
  avatarShape?: 'circle' | 'square';
  /** When true, security badge renders inline next to the name instead of in the health row. */
  inlineHealth?: boolean;
  /** Security badge octagon size in px. Forwarded to SecurityBadge via HealthDisplay. */
  securitySize?: number;
  /** Array-based health configs for multi-bar games (e.g., Chrono Core shields + life). Takes precedence over singular health fields. */
  healthConfigs?: HealthConfig[] | null;
  /** Per-config health values, parallel to healthConfigs. */
  healths?: number[] | null;
  /** Per-config health stages, parallel to healthConfigs. */
  healthStages?: (1 | 2)[] | null;
  /**
   * Per-player effective max values for each health config (overrides healthConfigs[i].maxValue).
   * Effective max for index i = healthMaxes[i] (if present and non-null), else stage-aware config max.
   */
  healthMaxes?: number[] | null;
  /**
   * Chrono Core pilot card thumbnail URL.
   * When this OR coreCardUrl is set, replaces PlayerAvatar with stacked circular avatars.
   */
  pilotCardUrl?: string | null;
  /**
   * Chrono Core core card thumbnail URL.
   * When this OR pilotCardUrl is set, replaces PlayerAvatar with stacked circular avatars.
   */
  coreCardUrl?: string | null;
  /**
   * Controls circle diameter for the pilot/core avatar zone.
   * 'standard' (default, PlayerWidget)   → 112px circles (~159px combined footprint at 58% overlap)
   * 'large'    (SidePlayersWidget)        → explicit diameter from avatarCircleDiameter prop:
   *   compact→200px (284px footprint, 16px margin in 300px usable)
   *   medium →224px (318px footprint, 12px margin in 330px usable)
   *   full   →248px (352px footprint,  8px margin in 360px usable)
   */
  thumbSize?: 'standard' | 'large';
  /**
   * Explicit pilot/core circle diameter in px for the side-player ('large') path.
   * Map: compact→200, medium→224, full→248. Defaults to 200 when absent.
   * Ignored when thumbSize === 'standard'. Set by SidePlayersWidget; do not compute locally.
   */
  avatarCircleDiameter?: number;
  /**
   * When true, mirrors StackedCardAvatars horizontally: pilot top-right, core bottom-left.
   * Use for P2 so both stacks face inward toward the center VS badge.
   */
  mirrorAvatarStack?: boolean;
}

/**
 * Composed block: avatar + name + archetype + health display + record.
 * Suitable for both the horizontal PlayerWidget and future vertical side-panel layouts.
 *
 * When pilotCardUrl or coreCardUrl is set, replaces the PlayerAvatar with
 * stacked overlapping circular avatars (pilot back/top-left, core front/bottom-right).
 * Circle diameter depends on thumbSize + avatarCircleDiameter:
 *   'standard'         → 112px (PlayerWidget, ~159px combined footprint at 58% overlap)
 *   'large' compact    → 200px (284px footprint, 16px margin in 300px usable)
 *   'large' medium     → 224px (318px footprint, 12px margin in 330px usable)
 *   'large' full       → 248px (352px footprint,  8px margin in 360px usable)
 *
 * The record Pill renders inline beside the player name/archetype block, not in the health row.
 */
export function PlayerInfoBlock({
  name,
  archetype,
  record,
  photoUrl,
  avatarSize = 64,
  showPhoto = true,
  glowActive = false,
  health,
  healthStage = 1,
  healthConfig,
  showHealth = true,
  align = 'left',
  avatarShape,
  inlineHealth = false,
  securitySize,
  healthConfigs,
  healths,
  healthStages,
  healthMaxes = null,
  pilotCardUrl,
  coreCardUrl,
  thumbSize = 'standard',
  avatarCircleDiameter,
  mirrorAvatarStack = false,
}: PlayerInfoBlockProps) {
  const isRight = align === 'right';
  const isCenter = align === 'center';

  // Determine whether to use the array-based (multi-bar) path or the single-config path.
  // Array path takes precedence when healthConfigs is a non-empty array with matching parallel arrays.
  const useArrayPath = healthConfigs != null
    && healthConfigs.length > 0
    && healths != null
    && healths.length === healthConfigs.length;

  // Single-config path: inline security logic (array path never uses inline)
  const isSecurity = !useArrayPath && healthConfig?.displayMode === 'security';
  const securityInline = isSecurity && inlineHealth;

  const healthMax = healthStage === 2
    ? (healthConfig?.secondStageMax ?? healthConfig?.maxValue ?? 0)
    : (healthConfig?.maxValue ?? 0);

  const rowJustify = isCenter ? 'center' : isRight ? 'flex-end' : 'flex-start';
  const textAlign = isCenter ? 'center' : isRight ? 'right' : 'left';
  // HealthDisplay/SecurityBadge only accept 'left' | 'right' — map center to left
  const healthAlign = isRight ? 'right' as const : 'left' as const;

  /**
   * Renders the stacked health bars for the array-based multi-bar path.
   * Effective max for each bar = healthMaxes[i] (if present) else stage-aware config max.
   */
  const renderStackedHealth = () => {
    if (!useArrayPath || !healthConfigs || !healths) return null;
    const stages = healthStages ?? healthConfigs.map(() => 1 as const);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
        {healthConfigs.map((cfg, i) => {
          const stage = stages[i] ?? 1;
          // Stage-aware config max (preserves secondStageMax behavior as fallback)
          const configMax = stage === 2 ? (cfg.secondStageMax ?? cfg.maxValue) : cfg.maxValue;
          // Per-player max overrides when available: healthMaxes[i] guards length and null
          const effectiveMax = (healthMaxes != null && healthMaxes.length > i && healthMaxes[i] != null)
            ? (healthMaxes[i] as number)
            : configMax;
          return (
            <HealthDisplay
              key={cfg.label}
              current={healths[i] ?? 0}
              max={effectiveMax}
              stage={stage}
              config={{ ...cfg, label: cfg.label }}
              barWidth="100%"
              align={healthAlign}
              badgeSize={securitySize}
            />
          );
        })}
      </div>
    );
  };

  // Circle diameter for pilot/core stacked avatar zone.
  // standard        → 112px, 58% overlap → 159px footprint; plus 8px margin = 175px in 180px PlayerWidget bubble.
  // large compact   → 200px, 58% overlap → 284px footprint (340px panel − 40px h-padding = 300px usable; 16px margin).
  // large medium    → 224px, 58% overlap → 318px footprint (370px panel − 40px h-padding = 330px usable; 12px margin).
  // large full      → 248px, 58% overlap → 352px footprint (400px panel − 40px h-padding = 360px usable;  8px margin).
  // avatarCircleDiameter is set by SidePlayersWidget (compact→200, medium→224, full→248); defaults to 200.
  const circleSize = thumbSize === 'large' ? (avatarCircleDiameter ?? 200) : 112;

  // Show pilot/core stacked avatars instead of PlayerAvatar when either URL is set
  const hasThumbnails = (pilotCardUrl != null && pilotCardUrl !== '') || (coreCardUrl != null && coreCardUrl !== '');

  return (
    <>
      {showPhoto && (
        hasThumbnails ? (
          // ChronoCore: stacked overlapping circular avatars.
          // The 8px margin wrapper adds breathing room above and below the avatar zone.
          // For standard (112px circles, 159px footprint): flex centering in the 180px PlayerWidget
          // bubble + 8px margin → ~10px visual gap each side. For large (204px): panel has plenty
          // of vertical space, margin just prevents the stack from sitting flush against neighbors.
          <div style={{ marginTop: 8, marginBottom: 8 }}>
            <StackedCardAvatars
              pilotCardUrl={pilotCardUrl}
              coreCardUrl={coreCardUrl}
              circleSize={circleSize}
              mirror={mirrorAvatarStack}
              avatarShape={avatarShape}
            />
          </div>
        ) : (
          <PlayerAvatar
            photoUrl={photoUrl}
            name={name || '?'}
            size={avatarSize}
            glowActive={glowActive}
            shape={avatarShape}
          />
        )
      )}
      <div style={{ minWidth: 0, flex: 1, textAlign, width: '100%' }}>
        {/* Name row — record Pill and security badge sit inline beside the name/archetype block */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flexDirection: isRight ? 'row-reverse' : 'row',
          justifyContent: isCenter ? 'center' : undefined,
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
            {/* Name: shrinks 30→18px to fit one line; wraps at 18px floor if still overflowing.
                Uses fonts.display (Fjalla One) — player names are large, prominent, short strings
                and are the primary headline element in every widget. fontFamily is passed explicitly
                so the measurement probe also uses Fjalla One (it's condensed; measuring in the
                wrong font would size the text incorrectly). */}
            <AutoShrinkText
              text={name || (isRight ? 'Player 2' : 'Player 1')}
              maxFontSize={30}
              minFontSize={18}
              fontWeight={700}
              lineHeight={1.3}
              textAlign={textAlign}
              fontFamily={fonts.display}
            />
            {/* Archetype: shrinks 15→11px to fit one line; wraps at 11px floor if still overflowing. */}
            {archetype && (
              <AutoShrinkText
                text={archetype}
                maxFontSize={15}
                minFontSize={11}
                fontWeight={500}
                lineHeight={1.2}
                color={glassText.secondary}
                textAlign={textAlign}
              />
            )}
          </div>
          {/* Record Pill — inline beside name/archetype (moved from health row) */}
          {record && (
            <span style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
              <Pill>{record}</Pill>
            </span>
          )}
          {securityInline && showHealth && healthConfig && health != null && (
            <HealthDisplay
              current={health}
              max={healthMax}
              stage={healthStage}
              config={healthConfig}
              align={healthAlign}
              badgeSize={securitySize}
            />
          )}
        </div>

        {/* Health row: array path (stacked bars) or single-config path — record no longer here */}
        {useArrayPath && showHealth ? (
          <div style={{ marginTop: 4, display: 'flex', gap: 12, justifyContent: rowJustify, alignItems: 'flex-start', width: '100%' }}>
            {renderStackedHealth()}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 12, marginTop: 4, justifyContent: rowJustify }}>
            {!securityInline && showHealth && healthConfig && health != null && (
              <HealthDisplay
                current={health}
                max={healthMax}
                stage={healthStage}
                config={healthConfig}
                align={healthAlign}
                badgeSize={securitySize}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}
